import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Avatar,
  IconButton,
  styled,
  Grid,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { darkColors } from "../../theme/colors";
import { assetIcons } from "../../constants/assets";
import { fontSizes } from "../../theme/font-sizes";
import AccountSidebar from "./account-sidebar";
import GradientButton from "../utils/gradient-button";
import { useNavigate } from "react-router-dom";
import routes from "../../constants/routes";
import apiRoutes from "../../api/routes";
import useApiRequest from "../../api/api";
import { compressImage } from "../utils/compress-image";
import { Upload } from "@aws-sdk/lib-storage";
import { S3Client } from "@aws-sdk/client-s3";
import { v4 as uuidv4 } from "uuid";
import {
  decodeJWTToken,
  extractNameIdentifier,
} from "../utils/decode-id-from-token";
import LoadingBackdrop from "../utils/loader";
import { useUser } from "../../context/user-context";
import InlineSvg from "../utils/inline-svg";
// Custom styled components

export const StyledTextField = styled(TextField)(({ isMediumScreen }) => ({
  width: isMediumScreen ? "100%" : 450,

  "& .MuiOutlinedInput-root": {
    padding: 10,

    color: "white",
    "& fieldset": {
      borderColor: "#333",
    },
    "&:hover fieldset": {
      borderColor: "#444",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#666",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#888",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontStyle: "italic",
  },
}));

export const StyledTextArea = styled(TextField)(({ isMediumScreen }) => ({
  width: isMediumScreen ? "100%" : 450,

  color: "#fff",

  "&:focus": {
    outline: "none",
    borderColor: "#666",
  },
  "& .MuiOutlinedInput-root": {
    padding: 10,

    color: "#fff",
    "& fieldset": {
      borderColor: "#333",
      height: 170,
    },
    "&:hover fieldset": {
      borderColor: "#444",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#666",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#888",
  },
}));
export const StyledTypography = styled(Typography)({
  fontSize: fontSizes.body, // Ensure fontSizes.body is defined
  color: darkColors.lightGray, // Ensure darkColors.lightGray is defined
  textAlign: "left",
  // width: 140,
});

export const AvatarWrapper = styled(Box)({
  position: "relative",

  width: "fit-content",
  "&:hover .edit-icon": {
    opacity: 0.8,
  },
});

const EditIconWrapper = styled(Box)({
  position: "absolute",
  right: -3,
  bottom: -3,
  background: "#161616",
  border: "1px solid",
  borderRadius: "99%",
  borderColor: darkColors.buttonTop,
  height: 30,
  width: 30,
  paddingInline: 0,
  cursor: "pointer",
  opacity: 1,
  transition: "opacity 0.2s",
});

export default function AccountComponent() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { makeApiCall } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const { user, updateUser } = useUser();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [isModified, setIsModified] = useState(false);

  const [profileData, setProfileData] = useState({
    displayName: "",
    bio: "",
    username: "",
    email: "",
    phone: "",
    photoURL: "",
    selectedFile: null, // Store the selected file temporarily
    fileExtension: "",
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const normalizeData = (data) => ({
    displayName: data.displayName || "",
    bio: data.bio || "",
    username: data.username || "",
    email: data.email || "",
    phone: data.phone || "",
    photoURL: data.imageURL || "", // Ensure consistency with storedUser
  });
  useEffect(() => {
    if (user) {
      setProfileData(normalizeData(user));
      setIsInitialLoad(false);
    }
  }, [user]);

  // Detect changes in profileData compared to user context
  useEffect(() => {
    if (!isInitialLoad) {
      const normalizedUser = normalizeData(user);
      setIsModified(
        JSON.stringify(profileData) !== JSON.stringify(normalizedUser)
      );
    }
  }, [profileData, user, isInitialLoad]);

  // Handle reset
  const handleReset = () => {
    setProfileData(normalizeData(user));
  };

  const navigateToSettings = () => {
    navigate(routes.settings);
  };
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;

    // Ensure input starts with '+' and only contains up to 12 digits
    if (inputValue.startsWith("+")) {
      const numericPart = inputValue.slice(1).replace(/\D/g, ""); // Remove non-numeric characters
      if (numericPart.length <= 12) {
        setProfileData({
          ...profileData,
          phone: `+${numericPart}`,
        });
      }
    }
  };

  const fileKeys = {
    temp: (id, extension) => `temp/${id}/${uuidv4()}.${extension}`,
    userProfilePicture: (id, extension) =>
      `users/${id}/${uuidv4()}.${extension}`,
    // other keys later....
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0]; // Using optional chaining to handle undefined

    if (!file) return; // If no file is selected, exit early

    // Store the selected file temporarily
    const fileExtension = file.name.split(".").pop();
    setProfileData({
      ...profileData,
      selectedFile: file, // Temporarily store the selected file
      fileExtension,
    });

    // Display the selected image immediately without compression (before saving)
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileData((prevData) => ({
        ...prevData,
        photoURL: reader.result, // Show the image preview (base64 string)
      }));
    };
    reader.readAsDataURL(file); // Create a base64 string of the selected image
  };

  const handleSave = async () => {
    const { selectedFile, fileExtension } = profileData;
    let finalPhotoURL = profileData.photoURL; // Default to the preview (base64)

    if (selectedFile) {
      // Check file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert("File size exceeds 5MB.");
        return;
      }
      setIsLoading(true);
      // Compress the image only when saving
      const compressedImage = await compressImage(selectedFile, fileExtension);
      const userId = extractNameIdentifier(user?.authToken);
      // Construct file key and CDN URL
      const fileKey = fileKeys.userProfilePicture(userId, fileExtension);
      const cdnUrl = `${process.env.REACT_APP_CLOUDFRONT_BASE}/${fileKey}`;

      finalPhotoURL = cdnUrl; // Use CDN URL after compression

      // Now upload to S3
      const target = {
        Bucket: process.env.REACT_APP_BUCKET,
        Key: fileKey,
        Body: compressedImage, // Compressed image
      };

      const creds = {
        accessKeyId: process.env.REACT_APP_ACCESS_KEY,
        secretAccessKey: process.env.REACT_APP_SECRET_KEY,
      };

      try {
        const parallelUploads3 = new Upload({
          client: new S3Client({
            region: process.env.REACT_APP_REGION,
            credentials: creds,
          }),
          leavePartsOnError: false,
          params: target,
        });

        parallelUploads3.on("httpUploadProgress", (progress) => {});
        // Call API to update user profile with the new photoURL
        await parallelUploads3.done(); // Wait for the upload to complete

        const payload = {
          displayName: profileData.displayName,
          bio: profileData.bio,
          phone: profileData.phone,
          photoURL: finalPhotoURL,
        };
        const response = await makeApiCall(
          apiRoutes.profile.account,
          "PUT",
          payload
        );

        if (response.success) {
          const updatedUser = {
            ...user,
            displayName: profileData.displayName,
            bio: profileData.bio,
            phone: profileData.phone,
            imageURL: finalPhotoURL, // Ensure photoURL is saved
          };

          localStorage.setItem("user", JSON.stringify(updatedUser));
          updateUser(updatedUser);
        }
      } catch (e) {
      } finally {
        setIsLoading(false);
        setIsModified(false); // Reset modification state after saving
      }
    } else {
      // If no new photoURL, simply update other profile data
      setIsLoading(true);
      try {
        const payload = {
          displayName: profileData.displayName,
          bio: profileData.bio,
          phone: profileData.phone,
          photoURL: finalPhotoURL, // Ensure photoURL is still passed in case it's not changed
        };
        const response = await makeApiCall(
          apiRoutes.profile.account,
          "PUT",
          payload
        );

        if (response.success) {
          const updatedUser = {
            ...user,
            displayName: profileData.displayName,
            bio: profileData.bio,
            phone: profileData.phone,
            imageURL: finalPhotoURL, // Ensure photoURL is saved
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          updateUser(updatedUser);
        }
      } catch (e) {
      } finally {
        setIsModified(false); // Reset modification state after saving
        setIsLoading(false);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#171717", height: "100vh" }}>
      {/* Sidebar */}
      {!isMediumScreen && <AccountSidebar />}
      <LoadingBackdrop isLoading={isLoading} />

      {/* Main content */}
      <Box
        // component="main"
        display={"flex-col"}
        sx={{
          //   flexGrow: 1,
          p: isMediumScreen ? 4 : 10,
          width: "100%",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
          },
          pt: isMediumScreen ? 5 : 8,
          mb: 7,
          bgcolor: "#121212",
          color: "#fff",
        }}
      >
        {/* <Container maxWidth=""> */}

        <Stack
          onClick={navigateToSettings}
          direction={"row"}
          sx={{ cursor: "pointer" }}
          alignItems="center"
        >
          {isMediumScreen && (
            <InlineSvg
              sx={{
                cursor: "pointer",
                display: "inline-block",
                width: 20,
                mr: 3,
                pb: 4,
              }}
              url={assetIcons.leftArrow}
            />
          )}
          <Typography
            fontFamily={"MontserratBold"}
            component="span"
            fontSize={
              isMediumScreen ? fontSizes.heading2 : fontSizes.superHeading5
            }
            textAlign={"left"}
            sx={{ mb: isMediumScreen ? 4 : 6 }}
            // onClick={(e) => e.stopPropagation()}
          >
            Profile Information
          </Typography>{" "}
        </Stack>

        <Grid container spacing={6}>
          {/* Profile Picture Section */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={"10%"}
              justifyContent={isMediumScreen ? "space-between" : "start"}
              alignItems="center"
            >
              <Stack>
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading3}
                  textAlign={"left"}
                  sx={{ width: 140 }}
                >
                  Profile Picture
                </Typography>
              </Stack>
              <Box sx={{ position: "relative" }}>
                <AvatarWrapper>
                  <Avatar
                    src={profileData.photoURL}
                    // alt="show"
                    sx={{
                      width: 68,
                      height: 68,
                      border: "2px solid",
                      borderColor: darkColors.buttonTop,
                    }}
                  />
                  <EditIconWrapper className="edit-icon">
                    <IconButton
                      component="label"
                      sx={{
                        padding: 0,
                        minWidth: "auto",
                        backgroundColor: "transparent",
                        color: "#6200ea", // Replace with darkColors.buttonTop
                      }}
                    >
                      <Edit
                        sx={{
                          color: darkColors.buttonTop,
                          fontSize: 20,
                          mt: 0.5,
                        }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </IconButton>
                  </EditIconWrapper>
                </AvatarWrapper>
              </Box>
            </Stack>
          </Grid>

          {/* Display Name Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : "10%"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Stack>
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading3}
                  textAlign={"left"}
                  sx={{ width: 140 }}
                >
                  Display Name
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {" "}
                {/* Added Stack to group the text field and subtext */}
                <StyledTextField
                  isMediumScreen={isMediumScreen}
                  value={profileData.displayName}
                  onChange={(e) => {
                    const maxLength = 25;
                    const inputValue = e.target.value;

                    if (inputValue.length <= maxLength) {
                      setProfileData({
                        ...profileData,
                        displayName: inputValue,
                      });
                    }
                  }}
                  variant="outlined"
                  size="small"
                />
                {/* Subtext for character count */}
                <Typography
                  fontFamily={"Montserrat"}
                  fontSize={fontSizes.info}
                  pl={1}
                  color="#888" // Optional: use a subtle color
                  textAlign="left"
                >
                  {profileData.displayName.length}/25 characters
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          {/* Bio Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : "10%"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Stack>
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading3}
                  textAlign={"left"}
                  sx={{ width: 140 }}
                >
                  Bio
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <StyledTextArea
                  value={profileData.bio}
                  variant="outlined"
                  multiline
                  rows={6}
                  size="small"
                  isMediumScreen={isMediumScreen}
                  onChange={(e) => {
                    const maxLength = 500;
                    const inputValue = e.target.value;

                    if (inputValue.length <= maxLength) {
                      setProfileData({
                        ...profileData,
                        bio: inputValue,
                      });
                    }
                  }}
                />
                <Typography
                  fontFamily={"Montserrat"}
                  fontSize={fontSizes.info}
                  pl={1}
                  color="#888" // Optional: use a subtle color
                  textAlign="left"
                >
                  {profileData.bio.length}/500 characters
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Box
          mt={20}
          sx={{
            maxWidth: isMediumScreen ? "100%" : "calc(10% + 590px)",
          }}
        >
          <Divider
            sx={{
              background: darkColors.gray,
              width: "100%",
            }}
          />
        </Box>

        <Typography
          fontFamily={"MontserratBold"}
          fontSize={fontSizes.superHeading5}
          textAlign={"left"}
          sx={{ mt: 6, mb: 4 }}
        >
          Account
        </Typography>

        <Grid container spacing={5}>
          {/* Username Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : "10%"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Typography
                fontFamily={"MontserratBold"}
                fontSize={fontSizes.heading3}
                textAlign={"left"}
                sx={{ width: 140 }}
              >
                Username
              </Typography>
              <StyledTextField
                isMediumScreen={isMediumScreen}
                value={profileData.username}
                variant="outlined"
                size="small"
                readOnly={true}
                sx={{ pointerEvents: "none" }}
                placeholder="Username"
                onChange={(e) =>
                  setProfileData({
                    ...profileData,
                    username: e.target.value,
                  })
                }
              />
            </Stack>
          </Grid>

          {/* Email Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : "10%"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Typography
                fontFamily={"MontserratBold"}
                fontSize={fontSizes.heading3}
                textAlign={"left"}
                sx={{ width: 140 }}
              >
                Email
              </Typography>
              <StyledTextField
                isMediumScreen={isMediumScreen}
                fullWidth
                value={profileData.email}
                readOnly={true}
                sx={{ pointerEvents: "none" }}
                variant="outlined"
                size="small"
                placeholder="mail@example.com"
                onChange={(e) =>
                  setProfileData({
                    ...profileData,
                    email: e.target.value,
                  })
                }
              />
            </Stack>
          </Grid>

          {/* Mobile Phone Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : "9.2%"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Typography
                fontFamily={"MontserratBold"}
                fontSize={fontSizes.heading3}
                textAlign={"left"}
                sx={{ width: 150 }}
              >
                Mobile Phone
              </Typography>
              <StyledTextField
                isMediumScreen={isMediumScreen}
                value={profileData.phone || ""}
                variant="outlined"
                size="small"
                type="number"
                id="num"
                // onChange={handlePhoneChange}
                onChange={(e) =>
                  setProfileData({
                    ...profileData,
                    phone: e.target.value,
                  })
                }
                onWheel={(e) => e.target.blur()}
                placeholder="+1234567890"
                inputProps={{
                  maxLength: 13, // '+123456789012' (1 for + and 12 for digits)
                }}
              />
            </Stack>
          </Grid>
        </Grid>
        <Box
          my={6}
          sx={{
            maxWidth: isMediumScreen ? "100%" : "calc(10% + 590px)",
          }}
        >
          <Divider
            sx={{
              background: darkColors.gray,
              width: "100%",
            }}
          />
        </Box>
        {isModified && (
          <Stack
            justifyContent={"space-between"}
            direction={"row"}
            sx={{
              maxWidth: isMediumScreen ? "100%" : "calc(10% + 590px)",
            }}
            border={`1px solid ${"#E94A25"}`}
            // height={34}
            borderRadius={"12px"}
            bgcolor={"#222222"}
            p={isMediumScreen ? 2 : 3}
          >
            <Typography
              textAlign={"left"}
              fontFamily={"MontserratBold"}
              fontSize={
                isMediumScreen ? fontSizes.heading3 : fontSizes.heading2
              }
              pt={isMediumScreen ? 0.6 : 0.7}
            >
              Save your changes?
            </Typography>
            <Stack direction={"row"} justifyContent={"end"}>
              <Button
                sx={{
                  fontFamily: "MontserratBold",
                  fontSize: fontSizes.heading3,
                }}
                onClick={handleReset}
              >
                Reset
              </Button>{" "}
              <GradientButton
                ml={isMediumScreen ? 1 : 3}
                width={isMediumScreen ? 100 : 120}
                text={"Save"}
                onClick={handleSave}
              />
            </Stack>
          </Stack>
        )}
        {/* </Container> */}
      </Box>
    </Box>
  );
}
